import React, { useMemo } from 'react';
import { PieChart, DollarSign, TrendingUp, Fuel, Building } from 'lucide-react';
import WidgetCard from './WidgetCard';
import { useDashboardStore } from '../../../store/useDashboardStore';
import { calculatePortfolioSummary } from '../../../services/portfolioService';

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${Math.round(value / 1000).toLocaleString()}K`;
  }
  return `$${Math.round(value).toLocaleString()}`;
}

function formatNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${Math.round(value / 1000).toLocaleString()}K`;
  }
  return Math.round(value).toLocaleString();
}

export default function PortfolioSummaryWidget() {
  const { valuations, isLoadingValuations, valuationsError } = useDashboardStore();

  const summary = useMemo(
    () => calculatePortfolioSummary(valuations),
    [valuations]
  );

  const stats = [
    {
      label: 'Total Portfolio Value',
      value: formatCurrency(summary.totalValue),
      icon: <DollarSign className="w-4 h-4" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      label: 'Properties Valued',
      value: summary.propertyCount.toString(),
      icon: <Building className="w-4 h-4" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      label: 'Avg. Value',
      value: formatCurrency(summary.avgValue),
      icon: <TrendingUp className="w-4 h-4" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      label: 'Total Annual Gallons',
      value: formatNumber(summary.totalAnnualGallons),
      icon: <Fuel className="w-4 h-4" />,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
  ];

  return (
    <WidgetCard
      title="Portfolio Summary"
      icon={<PieChart className="w-5 h-5" />}
      colSpan={2}
      isLoading={isLoadingValuations}
      error={valuationsError}
    >
      {summary.propertyCount === 0 ? (
        <p className="text-gray-500 text-sm">
          No valuations yet. Run your first valuation to see portfolio stats.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`${stat.bgColor} ${stat.color} p-1 rounded`}>
                    {stat.icon}
                  </div>
                  <span className="text-xs text-gray-500">{stat.label}</span>
                </div>
                <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
            ))}
          </div>

          {/* Highest / Lowest */}
          {summary.highestValued && summary.lowestValued && summary.propertyCount > 1 && (
            <div className="flex gap-3 text-xs">
              <div className="flex-1 bg-green-50 rounded-lg p-2">
                <span className="text-green-700 font-medium">Highest</span>
                <p className="text-gray-700 truncate">{summary.highestValued.location}</p>
                <p className="font-semibold text-green-700">
                  {formatCurrency(summary.highestValued.value)}
                </p>
              </div>
              <div className="flex-1 bg-red-50 rounded-lg p-2">
                <span className="text-red-700 font-medium">Lowest</span>
                <p className="text-gray-700 truncate">{summary.lowestValued.location}</p>
                <p className="font-semibold text-red-700">
                  {formatCurrency(summary.lowestValued.value)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </WidgetCard>
  );
}
